import type { UnpackResponse, SerializedObject } from "../../../api/src/lib";
import type { ActivityLogController } from "../../../api/src/activity-log/activity-log.controller";
import type { ActivityLog } from "../../../api/src/activity-log/lib";
import { deserializeDate, useAuthenticatedQuery } from "./api";

export type ActivityLogResponse = {
  data: ActivityLog[];
  total: number;
};

/**
 * The page the activity screens show: the first 50 entries, the page the
 * server answered before its reads took `limit`/`offset` from
 * `readPaginationParams` (APP-1649). The screens read one page; they do not
 * walk the whole log.
 */
const ACTIVITY_PAGE = { limit: "50", offset: "0" };

export function deserializeActivityLog(
  log: SerializedObject<ActivityLog>,
): ActivityLog {
  return {
    ...log,
    createdAt: deserializeDate(log.createdAt),
  };
}

/**
 * `total` is `pagination.totalCount`: the number of entries under the same
 * conditions, not the number on the page, as `total` was before APP-1649.
 */
function deserializeActivityLogPage(
  page: SerializedObject<UnpackResponse<ActivityLogController["getForFirm"]>>,
): ActivityLogResponse {
  return {
    data: page.data.map(deserializeActivityLog),
    total: page.pagination.totalCount,
  };
}

/** `path` with the first page and `action`, when given, as its query. */
function pageUrl(path: string, action: string | undefined) {
  const query = new URLSearchParams(ACTIVITY_PAGE);
  if (action) query.set("action", action);
  return `${path}?${query.toString()}`;
}

/** The first page of the caller's firm's activity log, at `/v1` only. */
export function useQueryFirmActivities(params?: { action?: string }) {
  return useAuthenticatedQuery<
    UnpackResponse<ActivityLogController["getForFirm"]>,
    ActivityLogResponse
  >({
    queryKey: ["v1", "activity-log", "firm", params],
    queryFn: async (page) => deserializeActivityLogPage(page),
    urlBuilderFn: () => pageUrl("/v1/activity-log", params?.action),
  });
}

/** The first page of one household's activity log, at `/v1` only. */
export function useQueryHouseholdActivities(
  householdId: number,
  params?: { action?: string },
) {
  return useAuthenticatedQuery<
    UnpackResponse<ActivityLogController["getForHousehold"]>,
    ActivityLogResponse
  >({
    queryKey: ["v1", "activity-log", "household", householdId, params],
    queryFn: async (page) => deserializeActivityLogPage(page),
    urlBuilderFn: () =>
      pageUrl(`/v1/activity-log/household/${householdId}`, params?.action),
    queryOptions: {
      enabled: householdId > 0,
    },
  });
}

/** The first page of one account's activity log, at `/v1` only. */
export function useQueryAccountActivities(
  accountId: number,
  params?: { action?: string },
) {
  return useAuthenticatedQuery<
    UnpackResponse<ActivityLogController["getForAccount"]>,
    ActivityLogResponse
  >({
    queryKey: ["v1", "activity-log", "account", accountId, params],
    queryFn: async (page) => deserializeActivityLogPage(page),
    urlBuilderFn: () =>
      pageUrl(`/v1/activity-log/account/${accountId}`, params?.action),
    queryOptions: {
      enabled: accountId > 0,
    },
  });
}
